import React, { useEffect } from 'react';
import { Viewer, Worker, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { thumbnailPlugin } from '@react-pdf-viewer/thumbnail';
import ko_KR from '@react-pdf-viewer/locales/lib/ko_KR.json';

// Import styles
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/thumbnail/lib/styles/index.css';

import './PDFViewer.css';

const PDFViewer = ({
    fileUrl,
    fileName,
    initialPage = 0,
    defaultScale = SpecialZoomLevel.PageFit,
    showSidebar = true
}) => {
    // 캡쳐/인쇄 방지: Ctrl+P, Ctrl+S 차단
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 's')) {
                e.preventDefault();
            }
        };
        const handleContextMenu = (e) => e.preventDefault();

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('contextmenu', handleContextMenu);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('contextmenu', handleContextMenu);
        };
    }, []);
    // 한국어 localization (줌 드롭다운 텍스트 커스터마이징)
    const localization = {
        ...ko_KR,
        zoom: {
            ...ko_KR.zoom,
            actualSize: "원본 크기",
            pageFit: "페이지 맞춤",
            pageWidth: "페이지 너비",
        }
    };

    // Thumbnail plugin for better page thumbnails
    const thumbnailPluginInstance = thumbnailPlugin({
        thumbnailWidth: 100,
    });

    // Default layout plugin with customized settings
    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        sidebarTabs: (defaultTabs) =>
            showSidebar ? [defaultTabs[0]] : [], // 썸네일 탭만 표시
        setInitialTab: () => Promise.resolve(0), // 사이드바 썸네일 탭을 기본으로 열기
        toolbarPlugin: {
            fullScreenPlugin: {
                enableShortcuts: true,
            },
            getFilePlugin: {
                fileNameGenerator: () => {
                    return fileName || 'document.pdf';
                },
            },
            printPlugin: {
                enableShortcuts: false,
            },
        },
        renderToolbar: (Toolbar) => (
            <Toolbar>
                {(slots) => {
                    const {
                        CurrentPageInput,
                        EnterFullScreen,
                        GoToNextPage,
                        GoToPreviousPage,
                        NumberOfPages,
                        ShowSearchPopover,
                        Zoom,
                        ZoomIn,
                        ZoomOut,
                    } = slots;
                    return (
                        <div className="pdf-toolbar-container">
                            <div className="pdf-toolbar-left">
                                <ShowSearchPopover />
                            </div>
                            <div className="pdf-toolbar-center">
                                <GoToPreviousPage />
                                <CurrentPageInput />
                                <span className="pdf-page-separator">/</span>
                                <NumberOfPages />
                                <GoToNextPage />
                            </div>
                            <div className="pdf-toolbar-right">
                                <ZoomOut />
                                <Zoom />
                                <ZoomIn />
                                <EnterFullScreen />
                            </div>
                        </div>
                    );
                }}
            </Toolbar>
        ),
    });

    return (
        <div className="pdf-viewer-container">
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                <Viewer
                    fileUrl={fileUrl}
                    plugins={[defaultLayoutPluginInstance, thumbnailPluginInstance]}
                    defaultScale={defaultScale}
                    initialPage={initialPage}
                    localization={localization}
                    theme={{
                        theme: 'light',
                    }}
                />
            </Worker>
        </div>
    );
};

export default PDFViewer;
